import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const santanderCaseStudy: ContentObject = {
    id: 'santander-uk-genai-devsecops',
    kind: 'system',
    type: 'case-study',
    name: 'GenAI Enhances Threat Modeling & DevSecOps at Santander UK',
    description:
        'Santander UK uses GenAI to automate threat modeling and security tasks, reducing turnaround times from days to hours and improving DevSecOps efficiency.',
    url: 'https://www.youtube.com/watch?v=cwZDqJFhYAo',
    scores: createScores({
        detailedMetrics: 4.0,
        relevanceToUseCase: 8.0,
        reliabilityOfSource: 9.0,
        strategicAndTacticalInformation: 9.0,
        technicalImplementationDetails: 9.0,
        noVendorBias: 9.0,
        relevanceToClient: 10.0,
    }),
    publishedAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000), // 11 days ago
    markdown: `# 1. Who the Source of Information Is
 
 Source Kind: YouTube 
 Title: "Practical Applications for Generative AI in Cybersecurity – Matt Adams"  
 Published at: August 6, 2023 
 Speaker: Matt Adams, Security Architect at Santander UK, with nearly 20 years experience and with responsibilities including defining global information security strategies. 
 
 Quote: 
 > "I found that what used to take me a couple of days I can now do in a couple of hours and I've tried to extrapolate that to see how we can apply it in business and how we can get those benefits across a broader set of people." 
 
 In this talk presented at the OWASP London event, Matt Adams provides a first-hand account of using generative AI as a productivity multiplier. Although his focus is on cybersecurity processes, the techniques he demonstrates—including automated threat modeling, iterative content refinement, and intelligent tool orchestration—directly impact how development teams (especially those working closely with security) enhance their efficiency.
 
 # 2. Successes
 
 The case study describes several areas where generative AI has been used to make processes faster, more consistent, and more scalable. Key successes include:
 
 ## Automated Threat Modeling: 
 Generative AI is used to simulate the presence of a security expert during threat modeling sessions. By feeding application descriptions into a prompt template, the system produces a structured threat model complete with identified threats, potential impacts, and even mitigation suggestions. This "virtual security person" assists developers and engineers in understanding potential vulnerabilities without always needing a dedicated security expert in the room.
 
 Quote: 
 > "...is there a way that we can use generative AI to provide almost like a security person in the room for those developers. And so that's when I came up with stride GPT." 
 
 ## AI-Assisted Content Generation and Iterative Editing: 
 The talk details how a dual-AI system—one "writer" bot and one "editor" bot—can generate security awareness materials. This iterative process harnesses two personalities each specializing in different roles. The writer bot produces draft content while the editor refines it through detailed feedback loops. This method not only accelerates the creation of technical documentation, but also leads to higher-quality, security-focused outputs.
 
 Quote: 
 > "By just tasking each instance of the model with one job you allow it to specialize and focus a lot more—so you get some additional focus from the language model." 
 
 ## Autonomous Agent-Based Execution for Security Scanning: 
 In one use case, the system is provided with only a high-level description (for example, "a very fast nmap scan") and then autonomously decides which tools, commands, and procedures to invoke. This autonomous agent essentially drives the entire scanning process, generating actionable reports for pen testing within minutes. The automation removes repetitive manual work and makes the security testing process significantly more efficient.
 
 Quote: 
 > "It doesn't matter… the agent based on the description will look at the intent of the user and it will decide which tools to run and the order that's going to run them in in order to achieve the goal..." 
 
 # 3. Challenges
 
 While there are many successes, the talk does not shy away from discussing challenges encountered when working with generative AI:
 
 ## Inconsistency and Variability in Output: 
 One recurring challenge is that even with the same input parameters, outputs can vary—sometimes quite significantly. The implementation requires substantial effort in prompt engineering to ensure the language model remains consistent and produces structured results.
 
 Quote: 
 > "One of the joys of working with LLMs... is that you'll never get the same response twice unless you work pretty hard at your prompt engineering... that's where most of my time is spent." 
 
 ## Managing Hallucinations and Fabricated Data: 
 The speaker addresses the need for a "human in the loop"—especially during the iterative writing and editing cycle—to verify and fact-check outputs. Inaccuracies or "hallucinated" content (for example, fabricated statistics) are a known issue with generative AI models.
 
 Quote: 
 > "…I think it's easier just to give you 80% of what you need so you're better off spending time fact checking that – to pick up those problems that only a human can." 
 
 ## Tool Integration and Security Considerations: 
 Integrating generative AI with security-critical tasks (like triggering automated scans) poses risks. For instance, giving a language model access to shell commands or sensitive network data raises privacy and security concerns. Safeguards must be put in place when running such automated agents.
 
 Quote: 
 > "…I wouldn't recommend doing it on your own machine… this is running on a digital ocean droplet and it's targeting another digital ocean droplet in the same subnet." 
 
 # 4. Technical Details
 
 The implementation of AI-driven solutions discussed in the talk involves several technical components and integration points:
 
 ## Prompt Templates and Structured Output: 
 A central technical strategy is the use of carefully crafted prompt templates that standardize the request process and help shape the output in structured formats (such as JSON). This method is crucial to ensuring that the threat modeling process, for instance, produces reliable and parseable results.
 
 Quote: 
 > "…I've advised that it should provide [output] in a very specific output format so that it's possible to then go on and generate those mitigations during that second step." 
 
 ## Dual Persona System for Content Generation: 
 The demonstration includes a two-step process using two instances of generative AI (both using GPT 3.5 turbo as an example) with distinct roles. The "writer" persona generates initial content based on a subject, and then an "editor" persona reviews and refines the content iteratively. This system mimics a real human editorial process and improves overall output quality.
 
 Quote: 
 > "…the writer bot writes content about the subject and then the editor bot comes in to review and provide critiques—and we've set up a process where these two interact in a loop." 
 
 ## Autonomous Agent and Tool Orchestration: 
 For security scans, an AI agent is programmed to analyze a user's intent from a brief description and decide which scanning tool (e.g., nmap or Nikto) and command flags to run. The agent can also reveal its "thought process" by showing its decision-making logic, thus providing traceability of the commands executed and the corresponding outputs.
 
 Quote: 
 > "…the agent decides did I meet all of the things that the user asked me for or should I run another tool." 
 
 ## Infrastructure and Frameworks: 
 The demonstration uses frameworks like Langchain to orchestrate tool interactions. In addition, the solution runs on cloud infrastructure (for example, Digital Ocean droplets have been used to host the scanning tools safely and isolate them from critical systems). The architecture also allows switching between models (such as GPT-3.5 turbo and GPT-4) or even using alternative open-source AI models from Hugging Face if data sensitivity or cost is a concern.
 
 Quote: 
 > "…you can absolutely vary that. You can… have one using GPT-3.5, another GPT-4… or even an open source model like llama." 
 
 # 5. Results
 
 The generative AI solutions have delivered several measurable improvements:
 
 ## Drastic Reduction in Time Required for Repetitive Tasks: 
 Tasks that previously required hours or even days (e.g., threat modeling or generating code paired with security reviews) can now be completed in minutes. This not only accelerates the development cycle but also frees up human experts to concentrate on more complex challenges.
 
 Quote: 
 > "…what used to take me a couple of days I can now do in a couple of hours." 
 
 ## Enhanced Consistency and Standardization: 
 By leveraging prompt templates, the generative AI systems output reports in consistent formats, which then makes automated processing (such as mitigation generation or report finalization in HTML) much simpler and more reliable.
 
 Quote: 
 > "…you don't have to think about prompt engineering yourself; you just provide the application description and the application takes care of the rest." 
 
 ## Improved Collaboration Between Different Teams: 
 The work is designed to augment rather than replace human professionals. By automating mundane and repetitive tasks, the system helps bridge gaps between development and security teams, thereby allowing them to focus on high-value activities such as validating outputs and refining processes.
 
 Quote: 
 > "…it's not about replacing people, it's about making processes and teams work faster." 
 
 # 6. Vendors Used
 
 A number of vendors, tools, and frameworks are integrated within this ecosystem:
 
 ## OpenAI: 
 The generative AI models (e.g., GPT-3.5 turbo and optionally GPT-4) are accessed via OpenAI's API. These models form the core of the threat modeling, content generation, and autonomous scanning processes.
 
 Quote: 
 > "…we pass that as a formatted prompt to the Open AI API in this case." 
 
 ## Digital Ocean: 
 For running controlled scanning experiments and to safely execute shell commands, Digital Ocean droplets are used as dedicated environments.
 
 Quote: 
 > "…this is running on a digital ocean droplet and it's targeting another digital ocean droplet in the same subnet." 
 
 ## Langchain (and Langsmith): 
 The demonstration leverages frameworks such as Langchain to orchestrate the autonomous agents workflow. There is also mention of Langsmith, a tool for monitoring metrics and debugging calls behind the scenes, which aids in performance tracking and prompt refinement.
 
 Quote: 
 > "…a really interesting tool that's just been released by the same team that developed Langchain… it will even give you things like the latency of the calls." 
 
 ## Hugging Face (Alternative Model Hosting): 
 For organizations with privacy or data sensitivity concerns, Hugging Face is mentioned as an alternative source for open source generative models that can be hosted on private infrastructures (like Amazon SageMaker).
 
 Quote: 
 > "…if you want an open source model you can find it on Hugging Face… and deploy them onto a service like Amazon SageMaker." 
 
 # Conclusion
 
 This case study showcases how generative AI is being deployed by innovators like Matt Adams at Santander UK to make cybersecurity—and by extension, the software development and DevSecOps processes—more productive and efficient. By automating threat modeling, enabling iterative content generation through dual AI personas, and orchestrating autonomous scanning tasks, the approach not only slashes turnaround times (from days to hours) but also improves consistency and enables teams to focus on higher-level analysis. While challenges such as output variability and the risk of fabrications exist, the implementation strategy strongly emphasizes maintaining a "human in the loop" and using personalization and structured prompts to harness AI's full potential. This blend of technical sophistication, smart vendor integration, and forward-thinking process design is setting the stage for a new era of enhanced productivity in software development and security operations.`,
    references: [
        {
            id: '1',
            name: 'Practical Applications for Generative AI in Cybersecurity',
            author: 'Matt Adams (Santander UK)',
            year: '2023',
            publisher: 'OWASP London (via YouTube)',
            url: 'https://www.youtube.com/watch?v=cwZDqJFhYAo',
        },
    ],
};
